import api from '@/api';
import router from '@/router';
import i18n from '@/lang';
import { useAuthStore } from '@/stores/auth';

let isHandlingSessionExpiry = false;

function isAuthRequest(url: string): boolean {
  return /\/(login|logout|register|forgot-password|reset-password)/.test(url);
}

function shouldRedirectToLogin(): boolean {
  const currentRoute = router.currentRoute.value;

  if (currentRoute.name === 'login') {
    return false;
  }

  return !currentRoute.matched.some((record) => record.meta.isPublic);
}

async function redirectToLogin() {
  if (isHandlingSessionExpiry) {
    return;
  }

  isHandlingSessionExpiry = true;

  try {
    const authStore = useAuthStore();
    await authStore.setUser(null);
    await authStore.setLogged(false);
    await authStore.setAgency(null);

    const currentPath = router.currentRoute.value.fullPath;

    await router.replace({
      name: 'login',
      query: currentPath && currentPath !== '/login' ? { redirect: currentPath } : undefined,
    });
  } finally {
    isHandlingSessionExpiry = false;
  }
}

/**
 * Intercepts expired session responses (401 Unauthenticated and 419 CSRF)
 * so the user is redirected to login instead of staying on the current screen.
 */
export function setupSessionExpiryHandler() {
  api.apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;

      if (status === 419 || status === 401) {
        const url = String(error?.config?.url || '');
        const redirectToLoginPage = !isAuthRequest(url) && shouldRedirectToLogin();

        if (redirectToLoginPage) {
          const message = String(i18n.global.t('auth.session.expired'));

          if (error.response?.data && typeof error.response.data === 'object') {
            error.response.data.message = message;
          }

          await redirectToLogin();
        }
      }

      return Promise.reject(error);
    },
  );
}
